import React from 'react';
import { MdClose } from 'react-icons/md';

const ResultSettings = ({ onClose, settings }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-secondary-dark-bg rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-gray-200">Result Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <MdClose size={24} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Assessment Section */}
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl">
            <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-gray-200">Assessment Structure</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-medium mb-2 text-slate-900 dark:text-gray-200">Continuous Assessment (CA)</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Total Max Score: {settings?.assessment?.ca?.max_score}</p>
                <div className="mt-2">
                  {settings?.assessment?.ca?.cas.map((ca, index) => (
                    <div key={ca._id} className="text-sm text-gray-600 dark:text-gray-300">
                      CA {index + 1}: {ca.max_score} marks
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-medium mb-2 text-slate-900 dark:text-gray-200">Examination</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Max Score: {settings?.assessment?.exam?.max_score}</p>
              </div>
            </div>
          </div>

          {/* Grading System */}
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl">
            <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-gray-200">Grading System</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {settings?.grading?.grades.map((grade) => (
                <div key={grade._id} className="bg-white dark:bg-gray-700 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-slate-900 dark:text-gray-200">{grade.grade}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    {grade.min_score} - {grade.max_score}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Remarks and Skills */}
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl">
            <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-gray-200">Remarks & Skills</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-medium mb-2 text-slate-900 dark:text-gray-200">Available Ratings</h4>
                <div className="flex flex-wrap gap-2">
                  {settings?.remark?.ratings.map((rating, index) => (
                    <span key={index} className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100 rounded-full text-sm">
                      {rating}
                    </span>
                  ))}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-medium mb-2 text-slate-900 dark:text-gray-200">Skills Assessed</h4>
                <div className="flex flex-wrap gap-2">
                  {settings?.remark?.skills.map((skill, index) => (
                    <span key={index} className="px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100 rounded-full text-sm">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Result Configuration */}
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl">
            <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-gray-200">Result Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-900 dark:text-gray-200">Admin Approval Required</span>
                  <span className={`px-2 py-1 rounded-full text-sm ${settings?.result?.admin_approval ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {settings?.result?.admin_approval ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-900 dark:text-gray-200">Teacher Preview Enabled</span>
                  <span className={`px-2 py-1 rounded-full text-sm ${settings?.result?.teacher_preview ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {settings?.result?.teacher_preview ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-900 dark:text-gray-200">Result Released</span>
                  <span className={`px-2 py-1 rounded-full text-sm ${settings?.result?.is_result_released ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {settings?.result?.is_result_released ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-medium mb-2 text-slate-900 dark:text-gray-200">Payment Settings</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Parent Payment Threshold</span>
                    <span className="font-medium">{settings?.result?.parent_payment_threshold}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Flexpay Fee</span>
                    <span className="font-medium">{settings?.sla?.flexpay_fee}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Pay Now Fee</span>
                    <span className="font-medium">{settings?.sla?.paynow_fee}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">SMS Fee</span>
                    <span className="font-medium">₦{settings?.sla?.sms_fee}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultSettings; 